import React, { useEffect, useState } from 'react';

import { useGetOne } from 'react-admin';

import {
  Card,
  CardContent,
  Box,
  CardHeader,
  LinearProgress,
  Typography,
  Divider,
} from '@mui/material';

import ReactApexChart from 'react-apexcharts';
import { isEmpty } from 'lodash';
import SelectInputButton from '../../layout/inputFields/SelectInputButton';
import theme from '../../../theme';
import useHoverSeriesUpdateLabel from '../../../hooks/useHoverSeriesUpdateLabel';
import useCaseStatusColors from '../../../hooks/useCaseStatusColors';
import caseStatuses from '../../../constants/caseStatuses';

const useGetGraphData = (
  data: {
    numberCasesByStatus: {
      Open: number;
      Closed: number;
      'in-progress': number;
      pending: number;
      scheduled: number;
    };
  },
  chartRef: React.RefObject<ReactApexChart>,
) => {
  const [hasData, setHasData] = useState(false);
  const [options, setOptions] = useState<ReactApexChart['props']['options']>();
  const [series, setSeries] = useState<ReactApexChart['props']['series']>();

  const {
    getTotalOptions,
    attachLegendHoverEvents,
  } = useHoverSeriesUpdateLabel({ chartRef });

  const caseStatusColors = useCaseStatusColors();

  useEffect(() => {
    if (!data) return;

    const newSeries = [
      data.numberCasesByStatus.Open ?? 0,
      data.numberCasesByStatus['in-progress'] ?? 0,
      data.numberCasesByStatus.pending ?? 0,
      data.numberCasesByStatus.Closed ?? 0,
      data.numberCasesByStatus.scheduled ?? 0,
    ] satisfies ReactApexChart['props']['series'];

    const colors = [
      caseStatusColors[caseStatuses.OPEN],
      caseStatusColors[caseStatuses.IN_PROGRESS],
      caseStatusColors[caseStatuses.PENDING],
      caseStatusColors[caseStatuses.CLOSED],
      caseStatusColors[caseStatuses.SCHEDULED],
    ];

    const labels = ['Open', 'In progress', 'Pending', 'Closed', 'Scheduled'];

    const totalOptions = getTotalOptions({
      series: newSeries,
      colors,
      labels,
      totalColor: theme.palette.primary.main,
      totalLabel: 'Total cases',
    });

    const newOptions = {
      colors,
      labels,
      chart: {
        redrawOnParentResize: true,
        height: 400,
        zoom: {
          enabled: false,
        },
        toolbar: {
          show: false,
        },
        events: {
          mounted: attachLegendHoverEvents,
          updated: attachLegendHoverEvents,
        },
      },
      legend: {
        position: 'bottom',
        show: !isEmpty(data.numberCasesByStatus),
      },
      plotOptions: {
        pie: {
          donut: {
            labels: {
              show: true,
              total: {
                show: true,
                ...totalOptions,
              },
            },
          },
        },
      },
    } satisfies ReactApexChart['props']['options'];

    setHasData(true);
    setOptions(newOptions);
    setSeries(newSeries);
  }, [attachLegendHoverEvents, caseStatusColors, data, getTotalOptions]);

  return {
    hasData,
    options,
    series,
  };
};

const CasesAnalytics = () => {
  const [period, setPeriod] = useState('30');
  const { data, isLoading } = useGetOne('dashboard', { id: `cases/${period}` });

  const chartRef = React.useRef<ReactApexChart>(null);
  const { hasData, options, series } = useGetGraphData(data, chartRef);

  return (
    <Card
      variant="outlined"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'stretch',
        height: '100%',
      }}
    >
      <CardHeader
        title="Cases"
        action={(
          <SelectInputButton
            choices={[
              { id: '1', name: 'Last day' },
              { id: '7', name: 'Last week' },
              { id: '30', name: 'Last month' },
              { id: '90', name: 'Last quarter' },
              { id: '365', name: 'Last year' },
              { id: '99999', name: 'All time' },
            ]}
            value={period}
            setValue={setPeriod}
          />
        )}
      />
      <Divider />
      <CardContent
        sx={{
          flexGrow: 1,
        }}
      >
        {isLoading && <LinearProgress /> }
        {hasData && <ReactApexChart ref={chartRef} options={options} series={series} type="donut" height={300} />}
        {(!isLoading && !hasData) && (
          <Box margin={10} width="100%">
            <Typography align="center"> No cases available </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default CasesAnalytics;
